// 2026-10-01 — visual rebuild. Matches Home.js/Login.js's actual brand
// system (navy/gold palette, Playfair Display headings, Inter body) rather
// than bare MUI defaults. Same functionality as before: two dropdowns,
// upload + extract, schema-driven review form, Save/Download, and the
// Find Previous Conversion search tab.
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { formatErrorDetail } from "./validators";
import {
  Box, Typography, Button, TextField, Chip, Checkbox,
  FormControlLabel, FormGroup, Select, MenuItem, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Alert, Stack, Tooltip,
  InputAdornment, Slider, Radio, RadioGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"; // 2026-10-04 — per-field (i) tooltip, explained below at FieldLabel
import ArrowBackIcon from "@mui/icons-material/ArrowBack"; // 2026-10-04 — this page rolls its own TopBar (not PageShell), which never had a back-to-Home link
import NavUserMenu from "./NavUserMenu";
import ThemeToggle from "./ThemeToggle";
import api from "../api";

const SOURCE_LAB_OPTIONS = [
  { value: "reliant_scientific", label: "Reliant Scientific — PGx Test Requisition" },
];
const TARGET_TEMPLATE_OPTIONS = [
  { value: "labarium_proove_genomics", label: "Labarium — Proove Genomics Test Order Form" },
];

// 2026-10-04 — mirrors requisition_schema.py's REQUIRE_ONE_OF_PROFILE_FIELDS
// / validate_required() on the JS side, so Download can be disabled with a
// clear reason BEFORE the request round-trips to the backend, not just
// rejected after. The backend's own check (main.py's /api/requisition/
// download) is still the real enforcement — this is client-side UX only,
// never trusted alone.
const REQUIRE_ONE_OF_PROFILE_FIELDS = ["opioid_risk_selected", "drug_metabolism_selected", "pain_perception_selected"];

// 2026-10-12 fix — the conversion results table showed the raw backend
// status string with no plain-language meaning ("pending_review",
// "corrected"). Separate from LabOrdersView.js's own order-status map —
// this is the requisition-CONVERSION flow's own status field, a
// different state machine entirely.
const STATUS_DISPLAY = {
  pending_review: "Pending Review",
  corrected: "Updated",
  finalized: "Order Submitted",
};

function validateRequired(schema, correctedData) {
  const problems = [];
  for (const section of schema) {
    for (const field of section.fields) {
      if (field.required) {
        const value = correctedData[field.id];
        if (!(value && String(value).trim())) problems.push(`${field.label} is required.`);
      }
    }
  }
  if (!REQUIRE_ONE_OF_PROFILE_FIELDS.some((fid) => correctedData[fid] && correctedData[fid].length)) {
    problems.push("At least one profile (Opioid Risk, Drug Metabolism, or Pain Perception) must be ordered.");
  }
  return problems;
}

// Backend validation errors arrive in two different shapes:
//   1. HTTPException(422, {"errors": [...]}) — FastAPI puts that whole
//      object in `detail`, so a plain string render would show
//      "[object Object]"; the `detail.errors` branch below handles that.
//   2. FastAPI's own default RequestValidationError 422 body:
//      {"detail": [{"loc": [...], "msg": "...", "type": "..."}, ...]} —
//      `detail` itself is an array of objects. Rendering that directly
//      crashes React ("Objects are not valid as a React child"), so this
//      falls through to the shared formatErrorDetail helper, which knows
//      how to turn that array (and any other shape) into safe text.
function formatApiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
    return detail.errors.join(" ");
  }
  return formatErrorDetail(detail, fallback);
}

// 2026-10-04 fix — real bug, reported with evidence: every download saved
// as the literal file "requisition.pdf" no matter what patient it was —
// both call sites below hardcoded the save-as name instead of reading the
// real filename the backend already computed and sent in
// Content-Disposition (fixed server-side too, main.py's download_requisition
// — it was separately building its OWN stale name before this). Backend
// now also sends `expose_headers=["Content-Disposition"]` in its CORS
// config, without which the browser blocks JS from ever reading this
// header at all, so this function would silently always fall through to
// `fallback` even with the server-side fix in place.
function filenameFromResponse(res, fallback) {
  const raw = res?.headers?.["content-disposition"] || res?.headers?.["Content-Disposition"];
  if (!raw) return fallback;
  const match = /filename="?([^";]+)"?/i.exec(raw);
  return match ? match[1] : fallback;
}

const B = {
  navy: "#00357a", navyDark: "#001f4d", navyDeep: "#000e24",
  gold: "#c9a96e", goldLight: "#dfc08a",
};

// ── shared chrome, matching Home.js's navbar exactly ────────────────────────
function TopBar({ title, subtitle }) {
  const navigate = useNavigate();
  return (
    <Box sx={{
      bgcolor: "rgba(0,31,77,0.96)", backdropFilter: "blur(20px)",
      borderBottom: "1px solid rgba(201,169,110,0.2)", position: "sticky", top: 0, zIndex: 20,
    }}>
      <Box sx={{ height: 2, background: "linear-gradient(90deg, transparent, rgba(201,169,110,0.8) 30%, rgba(223,192,138,0.9) 50%, rgba(201,169,110,0.8) 70%, transparent)" }} />
      {/* 2026-10-04 fix — real bug, confirmed directly: this row had no
          flexWrap and no minWidth:0/truncation safety on its children — on
          a narrower viewport, the Home link + divider + title/subtitle +
          right-side icons together don't fit on one line and the row
          overflows past its container ("things coming out of boundary")
          instead of wrapping. flexWrap lets it stack gracefully instead;
          minWidth:0 + noWrap+ellipsis on the title stops a long title from
          forcing the row wide in the first place. */}
      <Box sx={{ maxWidth: 1100, mx: "auto", px: 3, py: 2.2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
          {/* 2026-10-04 — this page never had a back-to-Home link, unlike
              Report_Generation.js/ReportsDashboard.js/SummaryView.js, which
              all get it for free via PageShell. Matches PageShell's own
              treatment exactly rather than switching this page onto
              PageShell itself (lower-risk — this component's layout/props
              shape differs enough that a full swap isn't a drop-in). */}
          <Box
            onClick={() => navigate("/")}
            sx={{
              display: "flex", alignItems: "center", gap: 1,
              color: "rgba(255,255,255,0.55)", fontSize: 14.5,
              fontFamily: "'Inter', sans-serif",
              cursor: "pointer", borderRadius: "8px", px: 1.5, py: 0.7,
              transition: "all 0.2s",
              "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 15 }} />
            <span>Home</span>
          </Box>
          {/* 2026-10-04 fix — real bug, reported with evidence (screenshot
              showed a giant dark block where a thin divider line should
              be): MUI's sx prop treats a unitless width between 0 and 1 as
              a PERCENTAGE, not px — so "width: 1" was literally "width:
              100%", stretching this into a huge block instead of the 1px
              divider line it was meant to be. */}
          <Box sx={{ width: "1px", height: 22, bgcolor: "rgba(255,255,255,0.12)" }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.2em", color: B.gold, fontFamily: "'Inter', sans-serif" }}>
              {subtitle}
            </Typography>
            <Typography sx={{
              fontSize: 20, fontWeight: 700, color: "#fff", fontFamily: "'Playfair Display', serif", lineHeight: 1.2,
              whiteSpace: { xs: "normal", sm: "nowrap" }, overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {title}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ThemeToggle /><NavUserMenu />
        </Stack>
      </Box>
    </Box>
  );
}

function SectionCard({ title, children, isDark, compact }) {
  return (
    <Box sx={{
      borderRadius: "16px", overflow: "hidden", mb: 2.5,
      border: `1px solid ${isDark ? "rgba(201,169,110,0.18)" : "rgba(0,53,122,0.12)"}`,
      bgcolor: isDark ? "rgba(17,24,39,0.6)" : "#fff",
      boxShadow: isDark ? "none" : "0 2px 16px rgba(0,31,77,0.05)",
      height: compact ? "100%" : "auto",
    }}>
      <Box sx={{
        px: 3, py: 1.6, background: isDark
          ? "linear-gradient(135deg, rgba(0,53,122,0.25), rgba(201,169,110,0.08))"
          : "linear-gradient(135deg, rgba(0,53,122,0.05), rgba(201,169,110,0.05))",
        borderBottom: `1px solid ${isDark ? "rgba(201,169,110,0.15)" : "rgba(0,53,122,0.1)"}`,
      }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.04em", color: isDark ? B.goldLight : B.navy, fontFamily: "'Inter', sans-serif" }}>
          {title.toUpperCase()}
        </Typography>
      </Box>
      <Box sx={{ p: 3 }}>{children}</Box>
    </Box>
  );
}

// ── Multi-step branded loader — mirrors the real extraction pipeline's
//    actual passes (header → medications → ICD-10 → consent → supporting
//    pages), so the wait (which is genuinely ~60-90s across ~10 Vertex
//    calls) reads as real progress, not a stuck spinner. ───────────────────
const EXTRACTION_STEPS = [
  { label: "Reading patient & specimen info", ms: 6000 },
  { label: "Scanning the medication checklist", ms: 13000 },
  { label: "Checking diagnosis codes", ms: 25000 },
  { label: "Reviewing consent & signatures", ms: 10000 },
  { label: "Reading attached documents", ms: 15000 },
  { label: "Finishing up", ms: 999999 },
];

function ExtractionLoader({ isDark }) {
  const [stepIndex, setStepIndex] = useState(0);
  useEffect(() => {
    let cancelled = false;
    let i = 0;
    const advance = () => {
      if (cancelled || i >= EXTRACTION_STEPS.length - 1) return;
      setTimeout(() => {
        if (cancelled) return;
        i += 1;
        setStepIndex(i);
        advance();
      }, EXTRACTION_STEPS[i].ms);
    };
    advance();
    return () => { cancelled = true; };
  }, []);

  return (
    <Box sx={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      py: 8, borderRadius: "16px", mb: 2.5,
      border: `1px solid ${isDark ? "rgba(201,169,110,0.18)" : "rgba(0,53,122,0.12)"}`,
      bgcolor: isDark ? "rgba(17,24,39,0.6)" : "#fff",
    }}>
      <Box sx={{ position: "relative", width: 72, height: 72, mb: 3 }}>
        <motion.div
          style={{
            position: "absolute", inset: 0, borderRadius: "50%",
            border: `3px solid ${isDark ? "rgba(201,169,110,0.15)" : "rgba(0,53,122,0.1)"}`,
          }}
        />
        <motion.div
          style={{
            position: "absolute", inset: 0, borderRadius: "50%",
            border: "3px solid transparent", borderTopColor: B.gold, borderRightColor: B.gold,
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
        />
        <Box sx={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 22,
        }}>📝</Box>
      </Box>

      <AnimatePresence mode="wait">
        <motion.div key={stepIndex}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3 }}>
          <Typography sx={{
            fontSize: 14.5, fontWeight: 600, fontFamily: "'Inter', sans-serif",
            color: isDark ? "#e2e8f0" : B.navy, textAlign: "center",
          }}>
            {EXTRACTION_STEPS[stepIndex].label}…
          </Typography>
        </motion.div>
      </AnimatePresence>

      <Stack direction="row" spacing={0.8} sx={{ mt: 2.5 }}>
        {EXTRACTION_STEPS.slice(0, -1).map((_, i) => (
          <Box key={i} sx={{
            width: i === stepIndex ? 20 : 6, height: 6, borderRadius: 3,
            bgcolor: i <= stepIndex ? B.gold : (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,53,122,0.12)"),
            transition: "all 0.35s ease",
          }} />
        ))}
      </Stack>

      <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 2.5, fontFamily: "'Inter', sans-serif" }}>
        Handwriting extraction runs several focused AI passes — this takes about a minute.
      </Typography>
    </Box>
  );
}

// 2026-10-04 — every field in requisition_schema.py now carries a `help`
// string (what to enter/check, in plain language) and the 3 identity
// fields carry `required: true`. FieldLabel renders both uniformly: a red
// asterisk for required fields, and an (i) icon next to EVERY field that
// has help text — not just the previously-ambiguous "Selected" ones, per
// explicit instruction this should be everywhere. Hover-triggered (MUI
// Tooltip's default), not a permanently-visible caption, so the form
// doesn't get visually noisier than it already is.
function FieldLabel({ children, flagged, help, required }) {
  return (
    <Typography sx={{ fontSize: 13.5, fontWeight: 600, mb: 0.6, color: flagged ? "#c0392b" : "text.secondary", display: "flex", alignItems: "center", gap: 0.5 }}>
      {flagged && <WarningAmberIcon sx={{ fontSize: 14 }} />}
      {children}
      {required && <Box component="span" sx={{ color: "#c0392b" }}>*</Box>}
      {help && (
        <Tooltip title={help} arrow placement="top" enterTouchDelay={0}>
          <InfoOutlinedIcon sx={{ fontSize: 14.5, color: "text.disabled", cursor: "help", "&:hover": { color: "text.secondary" } }} />
        </Tooltip>
      )}
    </Typography>
  );
}

function SchemaField({ field, value, onChange, flagged }) {
  const box = {
    mb: 2.5, p: flagged ? 1.5 : 0, borderRadius: "10px",
    border: flagged ? "1px solid rgba(192,57,43,0.35)" : "none",
    bgcolor: flagged ? "rgba(192,57,43,0.05)" : "transparent",
  };
  const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.875rem" },
  };

  if (field.type === "date") {
    // 2026-10-05 fix — real bug, reported with evidence: this used to share
    // the plain "text" case, a bare TextField with no date semantics at
    // all — free-typed "7/7/26" strings, no calendar picker, no format
    // enforcement. Real HTML5 date input now: native calendar picker,
    // and the value is always a parseable YYYY-MM-DD string once set,
    // which is also what validate_required()/DOB_RE on both ends expect.
    return (
      <Box sx={box}>
        <FieldLabel flagged={flagged} help={field.help} required={field.required}>{field.label}</FieldLabel>
        <TextField type="date" size="small" fullWidth sx={fieldSx}
          InputLabelProps={{ shrink: true }}
          value={value || ""} onChange={(e) => onChange(field.id, e.target.value)} />
      </Box>
    );
  }

  if (field.type === "number" && field.unit === "%") {
    // 2026-10-04 — explicit request: this is a 0-100 confidence percentage
    // the patient wrote on the source form ("I am confident my pain will
    // fully resolve...") — a slider reads that range far more naturally
    // than a bare number box. Keeps the numeric value underneath (still
    // what gets saved/rendered into the PDF), just a nicer input for it.
    const numValue = value === "" || value == null ? 0 : Number(value);
    return (
      <Box sx={box}>
        <FieldLabel flagged={flagged} help={field.help} required={field.required}>{field.label}</FieldLabel>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 0.5 }}>
          <Slider
            size="small"
            value={numValue}
            min={0} max={100} step={5}
            onChange={(_, v) => onChange(field.id, String(v))}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
            sx={{ color: B.navy, flex: 1 }}
          />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: B.navy, minWidth: 44, textAlign: "right" }}>
            {numValue}%
          </Typography>
        </Box>
      </Box>
    );
  }

  if (field.type === "number") {
    // 2026-10-05 fix — real bug, reported with evidence: Height/Weight
    // used to share the plain "text" case too, accepting literally
    // anything typed ("ABCDFG" included). Real numeric input now (blocks
    // non-digit characters at the browser level) plus the unit shown as a
    // fixed suffix instead of typed into the value.
    return (
      <Box sx={box}>
        <FieldLabel flagged={flagged} help={field.help} required={field.required}>{field.label}</FieldLabel>
        <TextField type="number" size="small" fullWidth sx={fieldSx}
          value={value || ""} onChange={(e) => onChange(field.id, e.target.value)}
          InputProps={{ endAdornment: field.unit ? <InputAdornment position="end">{field.unit}</InputAdornment> : undefined }} />
      </Box>
    );
  }

  if (field.type === "text") {
    // 2026-10-05 fix — real bug, reported directly with evidence
    // (screenshots: email/phone/state/zip all accepted plain garbage
    // text, e.g. "sssssssssss" as a ZIP). field.format (set in
    // requisition_schema.py) switches these to the real HTML5 input
    // type/constraint for that kind of value — native browser validation,
    // not a custom regex layer.
    const format = field.format;
    const textFieldProps = { size: "small", fullWidth: true, sx: fieldSx, value: value || "" };
    if (format === "email") {
      return (
        <Box sx={box}>
          <FieldLabel flagged={flagged} help={field.help} required={field.required}>{field.label}</FieldLabel>
          <TextField {...textFieldProps} type="email" onChange={(e) => onChange(field.id, e.target.value)} />
        </Box>
      );
    }
    if (format === "phone") {
      return (
        <Box sx={box}>
          <FieldLabel flagged={flagged} help={field.help} required={field.required}>{field.label}</FieldLabel>
          <TextField {...textFieldProps} type="tel" placeholder="555-123-4567"
            onChange={(e) => onChange(field.id, e.target.value)} />
        </Box>
      );
    }
    if (format === "state") {
      return (
        <Box sx={box}>
          <FieldLabel flagged={flagged} help={field.help} required={field.required}>{field.label}</FieldLabel>
          <TextField {...textFieldProps} inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
            onChange={(e) => onChange(field.id, e.target.value.toUpperCase().slice(0, 2))} />
        </Box>
      );
    }
    if (format === "zip") {
      return (
        <Box sx={box}>
          <FieldLabel flagged={flagged} help={field.help} required={field.required}>{field.label}</FieldLabel>
          <TextField {...textFieldProps} inputProps={{ inputMode: "numeric", maxLength: 10 }}
            onChange={(e) => onChange(field.id, e.target.value.replace(/[^0-9-]/g, "").slice(0, 10))} />
        </Box>
      );
    }
    return (
      <Box sx={box}>
        <FieldLabel flagged={flagged} help={field.help} required={field.required}>{field.label}</FieldLabel>
        <TextField size="small" fullWidth sx={fieldSx} value={value || ""} onChange={(e) => onChange(field.id, e.target.value)} />
      </Box>
    );
  }

  if (field.type === "single_select") {
    // 2026-10-05 addition — real bug, reported directly with evidence
    // (screenshots: Sex showed M AND F both checked, Specimen Type showed
    // both types checked — the real source form only ever marks ONE box
    // for these). Renders as a real radio group — physically impossible
    // to select two — but stores the SAME [code] single-item-list shape
    // checkbox_group already uses, so requisition_pdf.py needs no special
    // case for this type (see that file's own comment on _render_field_value).
    const current = Array.isArray(value) ? value[0] : value;
    return (
      <Box sx={box}>
        <FieldLabel flagged={flagged} help={field.help} required={field.required}>{field.label}</FieldLabel>
        <RadioGroup row value={current || ""} onChange={(e) => onChange(field.id, [e.target.value])}>
          {field.options.map((opt) => (
            <FormControlLabel
              key={opt.code} sx={{ minWidth: 140, mr: 1.5 }}
              control={<Radio size="small" sx={{ color: B.navy, "&.Mui-checked": { color: B.navy } }} />}
              value={opt.code}
              label={<Typography sx={{ fontSize: 14, fontFamily: "'Inter', sans-serif" }}>{opt.label}</Typography>}
            />
          ))}
        </RadioGroup>
      </Box>
    );
  }

  if (field.type === "checkbox_group") {
    const selected = new Set(Array.isArray(value) ? value : []);
    // 2026-10-05 fix — real bug, reported directly with evidence
    // (screenshot: every Race option AND "Decline to answer" checked at
    // once). field.exclusive_code (requisition_schema.py — currently just
    // Race's "decline") marks one option as mutually exclusive with every
    // other option in the SAME group: picking it clears everything else;
    // picking anything else while it's active clears it. Every other
    // checkbox_group field (no exclusive_code set) behaves exactly as
    // before — unrestricted multi-select, which is correct for ICD-10
    // code grids, ORT history, etc.
    const toggle = (code) => {
      const exclusive = field.exclusive_code;
      if (exclusive && code === exclusive) {
        onChange(field.id, selected.has(code) ? [] : [code]);
        return;
      }
      const next = new Set(selected);
      if (exclusive) next.delete(exclusive);
      next.has(code) ? next.delete(code) : next.add(code);
      onChange(field.id, Array.from(next));
    };
    const renderOption = (opt) => (
      <FormControlLabel
        key={opt.code} sx={{ minWidth: 200, maxWidth: 340, mr: 1.5, alignItems: "flex-start" }}
        control={<Checkbox size="small" checked={selected.has(opt.code)} onChange={() => toggle(opt.code)}
          sx={{ color: B.navy, "&.Mui-checked": { color: B.navy }, pt: 0 }} />}
        label={<Typography sx={{ fontSize: 14, fontFamily: "'Inter', sans-serif", pt: 0.6 }}>
          {/* 2026-10-04 fix — real bug: this used to print "code — label"
              any time the two strings differed at all, which is right
              for ICD-10 grids ("Z79.891 — Long-term use...") but showed
              ugly, redundant pairs everywhere else ("alcoholism —
              Alcoholism", "yes — Y"). Only show the code prefix when it
              actually looks like a real ICD-10 code (has a digit) —
              plain option codes are just internal ids and never
              belong on screen. */}
          {/\d/.test(opt.code) ? `${opt.code} — ${opt.label}` : opt.label}
        </Typography>}
      />
    );
    // 2026-11-01 fix (v5 form) — drug_metabolism_codes grew from 19 flat
    // codes to 96, organized by the real form's own GROUP/category tags
    // (requisition_schema.py's DRUG_METABOLISM_CODES). Dumping all 96 into
    // one undifferentiated FormGroup (what every other checkbox_group
    // field below still correctly does — Opioid Risk/Pain Perception/
    // Race/ORT never carry "group", so they're unaffected) would be an
    // unreadable wall of checkboxes. When options DO carry "group" (only
    // true for this one field today), render grouped sections with
    // category sub-headings instead — a stacked list, not the PDF's fixed
    // 3-column page layout, since this is a scrolling web form, not a
    // printed page.
    if (field.options.length && field.options[0].group) {
      const groups = [];
      const byGroup = {};
      for (const opt of field.options) {
        if (!byGroup[opt.group]) { byGroup[opt.group] = []; groups.push(opt.group); }
        byGroup[opt.group].push(opt);
      }
      return (
        <Box sx={box}>
          <FieldLabel flagged={flagged} help={field.help} required={field.required}>{field.label}</FieldLabel>
          {groups.map((groupName) => {
            const categories = [];
            const byCat = {};
            for (const opt of byGroup[groupName]) {
              if (!byCat[opt.category]) { byCat[opt.category] = []; categories.push(opt.category); }
              byCat[opt.category].push(opt);
            }
            return (
              <Box key={groupName} sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, textTransform: "uppercase", color: B.navy,
                                   borderBottom: "1px solid #ccc", pb: 0.5, mb: 1 }}>
                  {groupName}
                </Typography>
                {categories.map((cat) => (
                  <Box key={cat} sx={{ mb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontStyle: "italic", fontSize: 13, mb: 0.5 }}>{cat}</Typography>
                    <FormGroup row sx={{ flexWrap: "wrap", gap: 0 }}>
                      {byCat[cat].map(renderOption)}
                    </FormGroup>
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      );
    }
    return (
      <Box sx={box}>
        <FieldLabel flagged={flagged} help={field.help} required={field.required}>{field.label}</FieldLabel>
        <FormGroup row sx={{ flexWrap: "wrap", gap: 0 }}>
          {field.options.map(renderOption)}
        </FormGroup>
      </Box>
    );
  }

  if (field.type === "radio_row") {
    const rowValue = value || {};
    return (
      <Box sx={box}>
        <FieldLabel flagged={flagged} help={field.help} required={field.required}>{field.label}</FieldLabel>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                {field.options.map((o) => <TableCell key={o.value} align="center" sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}>{o.label}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {field.statements.map((statement, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ fontSize: 14, maxWidth: 260 }}>{statement}</TableCell>
                  {field.options.map((o) => (
                    <TableCell key={o.value} align="center">
                      <Checkbox size="small" checked={rowValue[i] === o.value}
                        onChange={() => onChange(field.id, { ...rowValue, [i]: o.value })}
                        sx={{ color: B.navy, "&.Mui-checked": { color: B.navy } }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    );
  }

  if (field.type === "table") {
    const rows = Array.isArray(value) ? value : [];
    const updateCell = (rowIdx, colId, v) => onChange(field.id, rows.map((r, i) => (i === rowIdx ? { ...r, [colId]: v } : r)));
    return (
      <Box sx={box}>
        <FieldLabel flagged={flagged} help={field.help} required={field.required}>{field.label}</FieldLabel>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>{field.columns.map((c) => <TableCell key={c.id} sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}>{c.label}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i}>
                  {field.columns.map((c) => {
                    // 2026-10-05 fix — real bug, reported directly with
                    // evidence (screenshot: "6" entered under "Efficacy
                    // Rating (1-5)", no range check at all). A column can
                    // carry its own type="number"+min/max (requisition_
                    // schema.py) — only Efficacy Rating sets this today;
                    // every other column (Medication, Side Effects, Dose,
                    // Frequency) stays plain free text, unaffected.
                    const isNumber = c.type === "number";
                    return (
                      <TableCell key={c.id}>
                        <TextField size="small" variant="standard" value={row[c.id] || ""}
                          type={isNumber ? "number" : "text"}
                          inputProps={isNumber ? { min: c.min, max: c.max } : undefined}
                          onChange={(e) => {
                            let v = e.target.value;
                            if (isNumber && v !== "") {
                              const n = Math.max(c.min ?? -Infinity, Math.min(c.max ?? Infinity, Number(v)));
                              v = String(n);
                            }
                            updateCell(i, c.id, v);
                          }} />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Button size="small" onClick={() => onChange(field.id, [...rows, {}])}
          sx={{ mt: 1, color: B.navy, textTransform: "none", fontWeight: 600 }}>+ Add row</Button>
      </Box>
    );
  }
  return null;
}

// ── The three profile panels, side-by-side — matches the real Labarium
//    form's own "PROOVE OPIOID RISK / DRUG METABOLISM / PAIN PERCEPTION"
//    3-column layout, instead of one long stacked list. requisition_
//    schema.py's profile_selection section has 9 fields in a fixed
//    <prefix>_selected / <prefix>_codes / <prefix>_other triplet per
//    profile — grouped here by that known prefix, not inferred generically
//    (this section's shape is fixed, unlike the others). ───────────────────
const PROFILE_PANELS = [
  { prefix: "opioid_risk", title: "Opioid Risk" },
  { prefix: "drug_metabolism", title: "Drug Metabolism (PGx)" },
  { prefix: "pain_perception", title: "Pain Perception" },
];

function ProfilePanelsRow({ section, correctedData, updateField, flaggedFieldIds, isDark }) {
  const fieldById = Object.fromEntries(section.fields.map((f) => [f.id, f]));
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, letterSpacing: "0.1em", color: "text.secondary", mb: 1, fontFamily: "'Inter', sans-serif" }}>
        {section.title.toUpperCase()}
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {PROFILE_PANELS.map((panel) => {
          const selectedField = fieldById[`${panel.prefix}_selected`];
          const codesField = fieldById[`${panel.prefix}_codes`];
          const otherField = fieldById[`${panel.prefix}_other`];
          return (
            <Box key={panel.prefix} sx={{ flex: "1 1 280px", minWidth: 260 }}>
              <SectionCard title={panel.title} isDark={isDark} compact>
                {selectedField && (
                  <SchemaField field={selectedField} value={correctedData[selectedField.id]}
                    onChange={updateField} flagged={flaggedFieldIds.has(selectedField.id)} />
                )}
                {codesField && (
                  <SchemaField field={codesField} value={correctedData[codesField.id]}
                    onChange={updateField} flagged={flaggedFieldIds.has(codesField.id)} />
                )}
                {otherField && (
                  <SchemaField field={otherField} value={correctedData[otherField.id]}
                    onChange={updateField} flagged={flaggedFieldIds.has(otherField.id)} />
                )}
              </SectionCard>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// 2026-10-05 fix — real gap, reported with evidence: the Consents section
// used to render patient_signature_present/date and provider_signature_
// present/date as bare generic fields with NO indication of what the
// reviewer is actually confirming — just a checkbox and a date box. The
// real source form (and the rendered PDF, requisition_pdf.py's
// _render_consents_section) both show the actual legal paragraph text
// above each signature line. Same short paragraphs, verbatim, plus the
// full legal text (page 2 of the real form) available on demand via
// "View full consent text" rather than always-expanded, so the form
// doesn't balloon in length by default.
function ConsentsSection({ section, correctedData, updateField, flaggedFieldIds, isDark }) {
  const fieldById = Object.fromEntries(section.fields.map((f) => [f.id, f]));
  const [showFullText, setShowFullText] = useState(false);
  const paraSx = { fontSize: 13, lineHeight: 1.6, color: isDark ? "#cbd5e1" : "#334155", mb: 1.5 };
  return (
    <SectionCard title={section.title} isDark={isDark}>
      <Typography sx={paraSx}>
        By signing below, I understand, read, and voluntarily agree to the Consents and Authorizations
        section related to the Services being performed.
      </Typography>
      <SchemaField field={fieldById.patient_signature_present} value={correctedData.patient_signature_present}
        onChange={updateField} flagged={flaggedFieldIds.has("patient_signature_present")} />
      <SchemaField field={fieldById.patient_signature_date} value={correctedData.patient_signature_date}
        onChange={updateField} flagged={flaggedFieldIds.has("patient_signature_date")} />

      <Box sx={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`, my: 2.5 }} />

      <Typography sx={paraSx}>
        By signing below, I acknowledge that I am a licensed medical professional authorized to order, and
        that these Services are medically necessary.
      </Typography>
      <SchemaField field={fieldById.provider_signature_present} value={correctedData.provider_signature_present}
        onChange={updateField} flagged={flaggedFieldIds.has("provider_signature_present")} />
      <SchemaField field={fieldById.provider_signature_date} value={correctedData.provider_signature_date}
        onChange={updateField} flagged={flaggedFieldIds.has("provider_signature_date")} />

      <Button size="small" onClick={() => setShowFullText((v) => !v)}
        sx={{ mt: 2, textTransform: "none", fontWeight: 600, color: B.navy }}>
        {showFullText ? "Hide" : "View"} full consent text (Medical Necessity, Biobank, HIPAA Authorization)
      </Button>
      {showFullText && (
        <Box sx={{ mt: 2, p: 2, borderRadius: "10px", bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,53,122,0.03)" }}>
          <Typography sx={{ ...paraSx, fontWeight: 700, mb: 0.5 }}>Medical Necessity Acknowledgement</Typography>
          <Typography sx={paraSx}>
            I certify that: (1) the ordered test(s) are medically necessary based on the patient's diagnosis
            and clinical condition; (2) each test is directly tied to a specific clinical decision documented
            on the front of this form; (3) the results will be used in patient treatment (medication
            selection, dosing, or risk management); (4) the test(s) are not ordered for screening, research,
            or convenience; and (5) the diagnosis codes accurately reflect the patient's condition.
          </Typography>
          <Typography sx={paraSx}>
            <b>Pharmacogenomic Testing Justification (Required for PGx Orders)</b> — I confirm that
            pharmacogenomic testing is being ordered in the context of current or planned medication therapy.
            The specific medication(s) under consideration are listed on the front of this form and in any
            attachments, and test results will be used to guide drug selection and/or dosing.
          </Typography>
          <Typography sx={{ ...paraSx, fontWeight: 700, mb: 0.5 }}>Patient Consents and Authorizations</Typography>
          <Typography sx={paraSx}>
            <b>Patient Consent and Authorization.</b> By signing this Authorization, I voluntarily consent to
            having the Services performed by the Provider Parties (Labarium Diagnostic Systems, Inc., its
            affiliates, and partner laboratories) and to having the results made available to my healthcare
            professionals. I request and authorize the Provider Parties to use, access, and disclose my PHI
            for performing and reporting the Services, treatment/payment/healthcare operations, de-identified
            research and publication, and compliance with applicable law. Substance Use Disorder Treatment
            Records (42 C.F.R. Part 2) require a separate written consent and are not covered by this
            Authorization.
          </Typography>
          <Typography sx={paraSx}>
            <b>De-Identification and Archival.</b> PHI retained for purposes other than direct treatment is
            stored only in de-identified form and may be retained indefinitely for research, educational, and
            publication purposes, without financial compensation to me except as described under Biobank
            Participation.
          </Typography>
          <Typography sx={paraSx}>
            <b>Right to Revoke.</b> I may revoke this Authorization at any time by written notice to the
            Provider Parties; revocation is effective upon receipt and does not affect prior uses/disclosures.
          </Typography>
          <Typography sx={paraSx}>
            <b>Biobank Participation.</b> The Labarium Biobank stores specimens and associated data for future
            research, diagnostic test development, and other scientific/commercial purposes. Biobank
            Participation is entirely voluntary and independent of clinical Services — declining has no effect
            on clinical care. Fair market value compensation (a "Biobank Credit"), if applicable, will be
            disclosed separately in writing and will not exceed my actual out-of-pocket obligation for the
            Services. I may withdraw Biobank Participation at any time by written notice; withdrawal takes
            effect prospectively.
          </Typography>
          <Typography sx={paraSx}>
            <b>Assignment of Benefits and Rights.</b> I assign to the Provider Parties all rights, title, and
            interest in amounts payable by any payor or health plan for Services rendered, and assign related
            claims and causes of action to the extent permitted by law, without relieving me of liability for
            any copayments, deductibles, or other charges.
          </Typography>
          <Typography sx={paraSx}>
            <b>Transmission of My Information.</b> I consent to the use of unsecured email, text message, or
            other electronic communication between me, the Provider Parties, Labarium, and/or Proove™, and
            understand such messages are typically not confidential.
          </Typography>
          <Typography sx={{ ...paraSx, mb: 0 }}>
            <b>Indemnification.</b> By signing, I acknowledge I shall be liable for, and shall indemnify,
            defend, and hold harmless Proove™ and its Provider Parties from any liability, loss, claim,
            lawsuit, injury, cost, damage, or expense arising out of performance or nonperformance of any
            duties under this Agreement.
          </Typography>
        </Box>
      )}
    </SectionCard>
  );
}

const goldButtonSx = {
  background: `linear-gradient(135deg, ${B.navyDeep}, ${B.navy})`, color: "#fff",
  fontWeight: 700, textTransform: "none", borderRadius: "10px", px: 3,
  "&:hover": { background: `linear-gradient(135deg, ${B.navyDeep}, #004299)` },
};

function NewConversionTab({ isDark, resumeConversionId, onResumed }) {
  const [sourceLab, setSourceLab] = useState(SOURCE_LAB_OPTIONS[0].value);
  const [targetTemplate, setTargetTemplate] = useState(TARGET_TEMPLATE_OPTIONS[0].value);
  const [file, setFile] = useState(null);
  // Part 12 — required attestation upload. A second, independent file —
  // it never feeds OCR/extraction (unlike `file`), so it's uploaded via
  // its own endpoint, not bundled into the /api/requisition/extract
  // multipart request. pendingAttestationFile is the file chosen in the
  // TOP "Upload & Convert" card BEFORE a conversion exists yet — it is
  // ONLY ever consumed by handleExtract, immediately after a new
  // conversionId is created, and is never re-used for any other
  // conversion (see handleExtract's own comment — this was a real,
  // caught-before-integration wrong-patient bug: an earlier version used
  // a shared useEffect keyed on [conversionId, attestationFile], which
  // re-fired and re-uploaded a leftover file from a PREVIOUS conversion
  // onto a newly created one whenever conversionId changed without the
  // user re-picking the file). attestationUploaded tracks whether the
  // CURRENT conversion's attestation has actually been uploaded — not
  // just whether a file is chosen — since that's what the Generate/
  // Download gate below and the backend's own enforcement both care
  // about.
  const [pendingAttestationFile, setPendingAttestationFile] = useState(null);
  const [attestationUploaded, setAttestationUploaded] = useState(false);
  const [attestationUploading, setAttestationUploading] = useState(false);
  const [schema, setSchema] = useState([]);
  const [conversionId, setConversionId] = useState(null);
  const [correctedData, setCorrectedData] = useState({});
  const [needsReview, setNeedsReview] = useState({});
  const [sourcePdfUrl, setSourcePdfUrl] = useState(null);
  const [sourcePdfUnavailable, setSourcePdfUnavailable] = useState(false);
  // 2026-10-16 fix (Part 12 regression close-out) — a conversion resumed
  // from "Find Previous Conversion" can predate Order a Test Part 8 (when
  // the source PDF moved into GCS); such a record genuinely has no
  // source_pdf_gcs_path at all (has_source === false on the loaded
  // record). Defaults to true for the normal post-Extract flow, where the
  // source PDF is always uploaded as part of Extract itself.
  const [hasSourceOnRecord, setHasSourceOnRecord] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => { api.get("/api/requisition/schema").then((res) => setSchema(res.data.schema || [])); }, []);

  // 2026-10-01 — fetch the ORIGINAL uploaded source PDF the moment
  // extraction finishes, for the side-by-side comparison view below.
  // Revoke the previous blob URL on cleanup so repeated conversions in one
  // session don't leak object URLs.
  useEffect(() => {
    if (!conversionId) return;
    let url;
    setSourcePdfUnavailable(false);
    api.get(`/api/requisition/${conversionId}/source-pdf`, { responseType: "blob" }).then((res) => {
      url = window.URL.createObjectURL(res.data);
      setSourcePdfUrl(url);
    }).catch(() => {
      // 2026-10-16 fix — a resumed pre-Part-8 conversion with no source
      // PDF on file at all 404s here; without this, the loading spinner
      // below spun forever instead of telling the reviewer why.
      setSourcePdfUrl(null);
      setSourcePdfUnavailable(true);
    });
    return () => { if (url) window.URL.revokeObjectURL(url); };
  }, [conversionId]);

  // 2026-10-16 fix (Part 12 regression close-out) — Resume action from
  // "Find Previous Conversion". Loads an EXISTING conversion into this
  // same review panel, the exact same way handleExtract loads a brand-new
  // one (setConversionId + setCorrectedData) — the source-PDF effect just
  // above already re-fires on its own since it's keyed on conversionId,
  // so it needs no separate handling here. corrected_data_prefill is only
  // present on the response when the record was never actually Saved
  // (main.py's get_requisition recomputes it from raw_extraction in that
  // case) — a real saved corrected_data always wins. has_attestation is
  // read from the server record (not any client-only flag), so the status
  // chip below reflects reality even right after a page refresh.
  useEffect(() => {
    if (!resumeConversionId) return;
    let cancelled = false;
    setError(""); setSaved(false); setFile(null); setPendingAttestationFile(null);
    api.get(`/api/requisition/${resumeConversionId}`).then((res) => {
      if (cancelled) return;
      const record = res.data;
      setConversionId(record._id);
      setCorrectedData(record.corrected_data || record.corrected_data_prefill || {});
      setNeedsReview(record.raw_extraction?.needs_review || {});
      setAttestationUploaded(!!record.has_attestation);
      setHasSourceOnRecord(!!record.has_source);
    }).catch((err) => {
      if (cancelled) return;
      setError(formatApiError(err, "Could not load that conversion."));
    }).finally(() => {
      if (!cancelled) onResumed?.();
    });
    return () => { cancelled = true; };
  }, [resumeConversionId, onResumed]);

  // needs_review.icd10 is now a flat list of SOURCE-form codes the two
  // extraction runs disagreed on (see requisition_ocr.extract_all_icd10) —
  // no longer maps to one specific target field id, so it's surfaced as
  // its own note rather than a per-field highlight.
  const icd10DisagreedCodes = needsReview.icd10 || [];
  const flaggedFieldIds = new Set();
  const unmatchedIcd10 = correctedData._unmatched_source_items?.icd10_codes || [];
  const unmatchedEthnicity = correctedData._unmatched_source_items?.ethnicity || "";
  // 2026-10-04 — recomputed every render (cheap: a handful of fields),
  // drives both the warning banner below and the Download button's
  // disabled state. Only gates Download, not Save — a reviewer can still
  // save partial, in-progress work.
  // Part 12 — same gate now also requires the attestation PDF to have
  // actually been uploaded (attestationUploaded, not just "a file is
  // chosen") — matches the backend's own real enforcement in
  // download_requisition, which checks attestation_pdf_gcs_path on the
  // real record, not anything client-side.
  const validationProblems = validateRequired(schema, correctedData);
  if (conversionId && !attestationUploaded) {
    validationProblems.push("The attestation PDF must be uploaded before generating the final document.");
  }
  // 2026-10-16 fix (Part 12 regression close-out) — a conversion resumed
  // from "Find Previous Conversion" that predates Order a Test Part 8 has
  // no source PDF on file at all (hasSourceOnRecord is read from the
  // server's real has_source field, set by the resume effect above) —
  // the backend's own missing_required_attachments gate will always 422
  // this regardless of attestation, so surface it here too rather than
  // letting a reviewer attach a PHI attestation to a conversion that can
  // never actually be generated.
  if (conversionId && !hasSourceOnRecord) {
    validationProblems.push("No source requisition is on file for this conversion — re-run Extract to attach one.");
  }

  // Part 12 — uploads a given attestation file to a given (already-real)
  // conversionId. Pulled out as its own function, called directly from
  // handleExtract and from the review-panel file picker's onChange — NOT
  // from a useEffect keyed on [conversionId, attestationFile]. An earlier
  // version used that effect; it was a real, caught-before-integration
  // wrong-patient bug: the effect re-fires on EVERY conversionId change,
  // so a leftover file from a previous (or failed) conversion could get
  // silently uploaded onto a brand-new one the instant conversionId
  // changed, with no re-selection by the user — exactly the kind of
  // PHI-misattachment this piece exists to prevent. Calling this function
  // directly, only at the two real "a file was just chosen/created for
  // THIS specific conversion" moments, makes that structurally
  // impossible: there is no path left where it can fire against a
  // conversionId the caller didn't just hand it.
  const uploadAttestation = async (convId, attestationFile) => {
    setAttestationUploading(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", attestationFile);
      await api.post(`/api/requisition/${convId}/attestation`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setAttestationUploaded(true);
    } catch (err) {
      setAttestationUploaded(false);
      setError(formatApiError(err, "Attestation upload failed."));
    } finally {
      setAttestationUploading(false);
    }
  };

  const handleExtract = async () => {
    if (!file) { setError("Choose a PDF first."); return; }
    setLoading(true); setError(""); setSaved(false);
    setAttestationUploaded(false);
    setHasSourceOnRecord(true); // a fresh Extract always uploads a source PDF as part of the same request
    try {
      const form = new FormData();
      form.append("source_lab", sourceLab);
      form.append("target_template", targetTemplate);
      form.append("file", file);
      const res = await api.post("/api/requisition/extract", form, { headers: { "Content-Type": "multipart/form-data" } });
      const newConversionId = res.data.conversion_id;
      setConversionId(newConversionId);
      setAttestationUploaded(false);
      setCorrectedData(res.data.corrected_data_prefill || {});
      setNeedsReview(res.data.extraction?.needs_review || {});
      // pendingAttestationFile is ONLY ever consumed here, for the
      // conversion that was just created — never re-applied to any other
      // conversionId, and cleared immediately after (success or failure)
      // so it can never be picked up again later.
      if (pendingAttestationFile) {
        const fileToUpload = pendingAttestationFile;
        setPendingAttestationFile(null);
        await uploadAttestation(newConversionId, fileToUpload);
      }
    } catch (err) {
      setError(formatApiError(err, "Extraction failed."));
    } finally { setLoading(false); }
  };

  const updateField = useCallback((fieldId, value) => {
    setCorrectedData((prev) => ({ ...prev, [fieldId]: value }));
    setSaved(false);
  }, []);

  const handleSave = async () => {
    setLoading(true); setError("");
    try {
      await api.post("/api/requisition/save", { conversion_id: conversionId, corrected_data: correctedData });
      setSaved(true);
    } catch (err) { setError(formatApiError(err, "Save failed.")); }
    finally { setLoading(false); }
  };

  const handleDownload = async () => {
    setLoading(true); setError("");
    try {
      const res = await api.post("/api/requisition/download",
        { conversion_id: conversionId, corrected_data: correctedData }, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filenameFromResponse(res, "requisition.pdf"); a.click();
      window.URL.revokeObjectURL(url);
      setSaved(true);
    } catch (err) {
      // Blob-response errors arrive as a Blob too, not parsed JSON — read
      // it back out before formatting, or formatApiError sees nothing.
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          err.response.data = JSON.parse(text);
        } catch { /* fall through to the generic message below */ }
      }
      setError(formatApiError(err, "Download failed."));
    }
    finally { setLoading(false); }
  };

  return (
    <Box sx={{ maxWidth: conversionId ? 1760 : 900, mx: "auto", py: 4, px: 2, transition: "max-width 0.3s ease" }}>
      <SectionCard title="Upload & Convert" isDark={isDark}>
        <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Box sx={{ minWidth: 260 }}>
            <FieldLabel>Source form</FieldLabel>
            <Select size="small" fullWidth value={sourceLab} onChange={(e) => setSourceLab(e.target.value)}
              sx={{ borderRadius: "10px", fontSize: "0.85rem" }}>
              {SOURCE_LAB_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </Box>
          <Box sx={{ minWidth: 260 }}>
            <FieldLabel>Target template</FieldLabel>
            <Select size="small" fullWidth value={targetTemplate} onChange={(e) => setTargetTemplate(e.target.value)}
              sx={{ borderRadius: "10px", fontSize: "0.85rem" }}>
              {TARGET_TEMPLATE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <FieldLabel required help="The original third-party requisition PDF — this is what gets OCR-extracted and converted.">
              Source requisition
            </FieldLabel>
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}
              sx={{ borderRadius: "10px", textTransform: "none", borderColor: B.gold, color: isDark ? B.goldLight : B.navy, fontWeight: 600, maxWidth: 260 }}>
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {file ? file.name : "Choose PDF"}
              </Box>
              <input type="file" accept="application/pdf" hidden
                onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }} />
            </Button>
          </Box>
          {/* Part 12 — required attestation upload, chosen alongside the
              source PDF above. Never feeds OCR/extraction (unlike the
              source picker) — uploaded via its own endpoint, consumed
              directly by handleExtract once the new conversion exists
              (see uploadAttestation/pendingAttestationFile above). A
              reviewer who didn't have it ready yet can still attach/
              replace it from the review panel below once extraction
              finishes. */}
          <Box sx={{ minWidth: 200 }}>
            <FieldLabel required help="The signed attestation document for this conversion — a separate required file from the source requisition above.">
              Signed attestation
            </FieldLabel>
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}
              sx={{ borderRadius: "10px", textTransform: "none", borderColor: B.gold, color: isDark ? B.goldLight : B.navy, fontWeight: 600, maxWidth: 260 }}>
              {/* Deliberately does NOT read/write attestationUploaded —
                  that state belongs to the CURRENT conversion being
                  reviewed below, not to whatever is queued up here for
                  the NEXT one. An earlier version set
                  setAttestationUploaded(false) here too, which would
                  falsely flip an already-attested, currently-open
                  conversion back to "required" (and disable its
                  Download) the instant staff started picking a file for
                  their NEXT patient — the same two-jobs-one-state mixup
                  that caused the original wrong-patient bug, just
                  surfacing as operator confusion instead of a silent
                  misattachment this time. This button only ever reflects
                  pendingAttestationFile, nothing about any live
                  conversion's server-side status. */}
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {pendingAttestationFile ? pendingAttestationFile.name : "Choose Attestation PDF"}
              </Box>
              <input type="file" accept="application/pdf" hidden
                onChange={(e) => { setPendingAttestationFile(e.target.files?.[0] || null); e.target.value = ""; }} />
            </Button>
          </Box>
          {/* disabled also while attestationUploading: a Replace call for
              the conversion currently under review could still be
              in-flight — starting a brand-new Extract (and a brand-new
              conversionId) while that old upload's promise is still
              pending is exactly the kind of overlap that let stale
              attestation state bleed across conversions. Blocking Extract
              here, and blocking the review panel's own Replace control
              while `loading` below, means only one of the two async
              operations can ever be in flight at once. */}
          <Button variant="contained" onClick={handleExtract} disabled={loading || !file || attestationUploading} sx={{ ...goldButtonSx, alignSelf: "flex-end" }}>
            {loading && !conversionId ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Extract"}
          </Button>
        </Box>
      </SectionCard>

      <AnimatePresence>
        {error && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px" }}>{error}</Alert>
          </motion.div>
        )}
        {saved && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Alert severity="success" sx={{ mb: 2.5, borderRadius: "10px" }}>Saved.</Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {loading && !conversionId && <ExtractionLoader isDark={isDark} />}

      {conversionId && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          {/* 2026-10-01 — side-by-side comparison layout: the original
              uploaded source PDF stays visible (sticky) alongside the
              review form, so staff can see exactly what the source form
              says while correcting/adding anything the extraction missed
              or couldn't map to the target form. Stacks vertically on
              narrow screens rather than cramming two columns. */}
          <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexDirection: { xs: "column", lg: "row" } }}>
            <Box sx={{
              flex: { xs: "1 1 auto", lg: "0 0 42%" }, width: { xs: "100%", lg: "42%" },
              position: { lg: "sticky" }, top: { lg: 96 },
            }}>
              <SectionCard title="Original Source Document" isDark={isDark}>
                {sourcePdfUrl ? (
                  <Box sx={{
                    borderRadius: "10px", overflow: "hidden",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
                    height: "calc(100vh - 260px)", minHeight: 480,
                  }}>
                    <iframe title="Source requisition PDF" src={sourcePdfUrl} style={{ width: "100%", height: "100%", border: "none" }} />
                  </Box>
                ) : sourcePdfUnavailable ? (
                  // 2026-10-16 fix — a resumed pre-Part-8 conversion with no
                  // source PDF on file 404s on the fetch above; without
                  // this branch the spinner below spun forever with no
                  // explanation.
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 8, px: 2, textAlign: "center" }}>
                    <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                      No source requisition is on file for this conversion — re-run Extract to attach one.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 8 }}>
                    <CircularProgress size={22} sx={{ color: B.gold }} />
                  </Box>
                )}
              </SectionCard>
            </Box>

            <Box sx={{ flex: "1 1 auto", minWidth: 0, width: "100%" }}>
              {unmatchedIcd10.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2.5, borderRadius: "10px" }}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 0.5 }}>
                    {unmatchedIcd10.length} code{unmatchedIcd10.length === 1 ? "" : "s"} marked on the source form {unmatchedIcd10.length === 1 ? "has" : "have"} no matching checkbox on the Labarium form
                  </Typography>
                  <Typography sx={{ fontSize: 14 }}>
                    {unmatchedIcd10.map((c) => `${c.code} (${c.label})`).join(" · ")}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mt: 0.5, color: "text.secondary" }}>
                    Compare against the source PDF on the left and add these manually where appropriate — they can't be auto-checked since the Labarium form doesn't have an exact equivalent.
                  </Typography>
                </Alert>
              )}
              {unmatchedEthnicity && (
                <Alert severity="warning" sx={{ mb: 2.5, borderRadius: "10px" }}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 0.5 }}>
                    Ethnicity marked on the source form has no matching option on the Labarium form
                  </Typography>
                  <Typography sx={{ fontSize: 14 }}>
                    Source form shows: <b>{unmatchedEthnicity}</b> — select the closest option on the right manually, or note it elsewhere.
                  </Typography>
                </Alert>
              )}
              {icd10DisagreedCodes.length > 0 && (
                <Alert severity="info" sx={{ mb: 2.5, borderRadius: "10px" }}>
                  <Typography sx={{ fontSize: 14 }}>
                    Double-checked against the source PDF — these codes were uncertain: {icd10DisagreedCodes.join(", ")}
                  </Typography>
                </Alert>
              )}

              {schema.map((section) => {
                // Layout mirrors the real target PDF's own structure
                // rather than one generic stacked list for every section:
                // compact fields (patient/payment/specimen info) sit in a
                // grid like the real form's boxed field rows, and the
                // three profile panels render side-by-side exactly like
                // the Labarium form's own 3-column "Opioid Risk / Drug
                // Metabolism / Pain Perception" layout.
                if (section.id === "profile_selection") {
                  return (
                    <ProfilePanelsRow key={section.id} section={section} correctedData={correctedData}
                      updateField={updateField} flaggedFieldIds={flaggedFieldIds} isDark={isDark} />
                  );
                }
                if (section.id === "consents") {
                  return (
                    <ConsentsSection key={section.id} section={section} correctedData={correctedData}
                      updateField={updateField} flaggedFieldIds={flaggedFieldIds} isDark={isDark} />
                  );
                }
                if (section.id === "patient_info") {
                  // 2026-10-04 — explicit request: match the real source
                  // form's own horizontal row grouping, not a generic wrap.
                  // The real form lays this section out as: Sex/Height/
                  // Height/Weight/DOB/Phone on one row, Email alone on its
                  // own (full-width) row, Last/First/MI on one row, Address
                  // alone, City/State/Zip on one row, Race alone — so this
                  // mirrors that exact grouping rather than letting fields
                  // wrap wherever they happen to fit.
                  const ROWS = [
                    ["sex", "height_ft", "height_in", "weight_lbs", "dob", "phone"],
                    ["email"],
                    ["last_name", "first_name", "middle_initial"],
                    ["address"],
                    ["city", "state", "zip"],
                    ["race"],
                  ];
                  const byId = Object.fromEntries(section.fields.map((f) => [f.id, f]));
                  return (
                    <SectionCard key={section.id} title={section.title} isDark={isDark}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {ROWS.map((rowIds, i) => (
                          <Box key={i} sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                            {rowIds.map((id) => {
                              const field = byId[id];
                              if (!field) return null;
                              return (
                                <Box key={id} sx={{ flex: "1 1 160px", minWidth: 140 }}>
                                  <SchemaField field={field} value={correctedData[field.id]}
                                    onChange={updateField} flagged={flaggedFieldIds.has(field.id)} />
                                </Box>
                              );
                            })}
                          </Box>
                        ))}
                      </Box>
                    </SectionCard>
                  );
                }
                const compactSection = ["payment_info", "specimen_collection"].includes(section.id);
                return (
                  <SectionCard key={section.id} title={section.title} isDark={isDark}>
                    {compactSection ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {section.fields.map((field) => {
                          const wide = field.type === "checkbox_group" && field.options.length > 3;
                          return (
                            <Box key={field.id} sx={{ flex: wide ? "1 1 100%" : "1 1 220px", minWidth: wide ? "100%" : 200 }}>
                              <SchemaField field={field} value={correctedData[field.id]}
                                onChange={updateField} flagged={flaggedFieldIds.has(field.id)} />
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      section.fields.map((field) => (
                        <SchemaField key={field.id} field={field} value={correctedData[field.id]}
                          onChange={updateField} flagged={flaggedFieldIds.has(field.id)} />
                      ))
                    )}
                  </SectionCard>
                );
              })}
              {/* 2026-10-04 — mandatory-field banner. Only gates Download
                  (see validateRequired's own comment) — Save stays
                  available so partial, in-progress review work is never
                  lost. Same rule the backend enforces at
                  /api/requisition/download; this just explains it before
                  the click instead of after a rejected request. */}
              {validationProblems.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px" }}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 0.3 }}>Before downloading:</Typography>
                  {validationProblems.map((p, i) => <Typography key={i} sx={{ fontSize: 14 }}>• {p}</Typography>)}
                </Alert>
              )}
              {/* Part 12 — lets a reviewer attach/replace the attestation
                  here too, not just in the initial Upload & Convert card —
                  covers the case where staff didn't have it ready at
                  Extract time. Posts directly to THIS conversionId via
                  uploadAttestation (never through the removed effect), so
                  there's no way for it to target any other conversion.
                  Disabled while an upload is already in flight — two
                  overlapping uploads for the same conversion could finish
                  out of order server-side and leave the pointer on the
                  wrong file. */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Chip
                  size="small"
                  label={attestationUploading ? "Uploading attestation…" : attestationUploaded ? "Attestation attached" : "Attestation required"}
                  sx={{
                    fontWeight: 600,
                    bgcolor: attestationUploading ? "rgba(201,169,110,0.15)" : attestationUploaded ? "rgba(26,107,58,0.1)" : "rgba(192,57,43,0.1)",
                    color: attestationUploading ? B.navy : attestationUploaded ? "#1a6b3a" : "#c0392b",
                  }}
                />
                <Button component="label" size="small" variant="text" startIcon={<UploadFileIcon />} disabled={attestationUploading || loading}
                  sx={{ textTransform: "none", fontWeight: 600, color: isDark ? B.goldLight : B.navy }}>
                  {attestationUploaded ? "Replace attestation" : "Upload attestation"}
                  <input type="file" accept="application/pdf" hidden
                    onChange={(e) => {
                      const picked = e.target.files?.[0] || null;
                      e.target.value = "";
                      if (picked && conversionId) uploadAttestation(conversionId, picked);
                    }} />
                </Button>
              </Box>
              <Box sx={{ display: "flex", gap: 1.5, mt: 1, mb: 4 }}>
                <Button variant="outlined" startIcon={<SaveIcon />} onClick={handleSave} disabled={loading}
                  sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, borderColor: B.navy, color: isDark ? B.goldLight : B.navy }}>
                  Save
                </Button>
                <Tooltip title={validationProblems.length > 0 ? validationProblems.join(" ") : ""} arrow placement="top">
                  <span>
                    <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}
                      disabled={loading || validationProblems.length > 0} sx={goldButtonSx}>
                      Download
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </motion.div>
      )}
    </Box>
  );
}

function FindPreviousTab({ isDark, onResume }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    setLoading(true); setError("");
    try {
      const res = await api.get("/api/requisition/search", { params: { first_name: firstName, last_name: lastName, dob } });
      setResults(res.data.results || []);
    } catch (err) { setError(formatApiError(err, "Search failed.")); }
    finally { setLoading(false); }
  };

  const handleDownload = async (conversionId) => {
    const res = await api.get(`/api/requisition/${conversionId}/download`, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filenameFromResponse(res, "requisition.pdf"); a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ maxWidth: 820, mx: "auto", py: 4, px: 2 }}>
      <SectionCard title="Find a previous conversion" isDark={isDark}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
          <TextField label="First name" size="small" value={firstName} onChange={(e) => setFirstName(e.target.value)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField label="Last name" size="small" value={lastName} onChange={(e) => setLastName(e.target.value)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField label="Date of birth" size="small" value={dob} onChange={(e) => setDob(e.target.value)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} disabled={loading} sx={goldButtonSx}>Search</Button>
        </Box>
      </SectionCard>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{error}</Alert>}

      <Box sx={{ borderRadius: "16px", overflow: "hidden", border: `1px solid ${isDark ? "rgba(201,169,110,0.18)" : "rgba(0,53,122,0.12)"}`, bgcolor: isDark ? "rgba(17,24,39,0.6)" : "#fff" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 12.5, letterSpacing: "0.04em", color: isDark ? B.goldLight : B.navy } }}>
              <TableCell>PATIENT</TableCell><TableCell>SOURCE LAB</TableCell><TableCell>STATUS</TableCell><TableCell align="right">ACTION</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5, color: "text.secondary", fontSize: 14.5 }}>No results.</TableCell></TableRow>
            ) : results.map((r) => {
              // 2026-10-16 fix (Part 12 regression close-out) — a
              // conversion that isn't finalized yet (status !== "finalized",
              // same real signal final_pdf_blob_path's own disabled-state
              // check below already uses) has no way back into the review
              // panel at all today — staff's only option was re-running
              // Extract from scratch, which creates a brand-new Mongo
              // document and orphans the original source GCS blob. Resume
              // loads this exact record (conversion_id + corrected_data)
              // into the New Conversion tab's review panel, same
              // mechanism handleExtract already uses after a fresh Extract.
              //
              // Keyed on final_pdf_blob_path (the SAME real signal the
              // Download button below already uses), not status — status
              // flips to "corrected" the moment save_correction runs
              // inside download_requisition, which happens BEFORE the
              // attestation fetch/render/archive steps. A conversion that
              // already has a real archived PDF but whose latest Download
              // attempt failed partway through (e.g. the new fail-closed
              // attestation-fetch error, or a render/archive failure) would
              // otherwise show "Open" instead of "Download" here, even
              // though a real finalized archive already exists for it.
              const canResume = !r.final_pdf_blob_path;
              return (
                <TableRow key={r._id} hover>
                  <TableCell>{r.patient_first_name} {r.patient_last_name}</TableCell>
                  <TableCell>{r.source_lab}</TableCell>
                  <TableCell><Chip size="small" label={STATUS_DISPLAY[r.status] || r.status} sx={{ fontWeight: 600, bgcolor: r.status === "finalized" ? "rgba(26,107,58,0.1)" : r.status === "corrected" ? "rgba(33,150,243,0.12)" : "rgba(201,169,110,0.15)", color: r.status === "finalized" ? "#1a6b3a" : r.status === "corrected" ? "#1565c0" : B.navy }} /></TableCell>
                  <TableCell align="right">
                    {canResume ? (
                      <Button size="small" onClick={() => onResume(r._id)}
                        sx={{ textTransform: "none", fontWeight: 600, color: B.navy }}>Open</Button>
                    ) : (
                      <Button size="small" disabled={!r.final_pdf_blob_path} startIcon={<DownloadIcon />} onClick={() => handleDownload(r._id)}
                        sx={{ textTransform: "none", fontWeight: 600, color: B.navy }}>Download</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

export default function RequisitionConversion() {
  const [tab, setTab] = useState(0);
  // 2026-10-16 fix (Part 12 regression close-out) — Resume action. New
  // Conversion and Find Previous Conversion are sibling tabs with
  // independent state; this is lifted up here just far enough to let a
  // click in Find Previous Conversion hand a conversion_id to the New
  // Conversion tab's own review panel and switch to it. onResumed clears
  // it back to null once NewConversionTab has consumed it, so it never
  // re-fires on its own (e.g. if the reviewer later flips tabs again).
  const [resumeConversionId, setResumeConversionId] = useState(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const tabBtn = (idx, label) => (
    <Box onClick={() => setTab(idx)} sx={{
      px: 2.5, py: 1.2, cursor: "pointer", fontSize: 14.5, fontWeight: 700, fontFamily: "'Inter', sans-serif",
      color: tab === idx ? (isDark ? "#fff" : B.navy) : "text.secondary",
      borderBottom: tab === idx ? `2px solid ${B.gold}` : "2px solid transparent",
      transition: "all 0.2s",
    }}>
      {label}
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#080d1a" : "#f4f7ff", fontFamily: "'Inter', sans-serif" }}>
      {/* 2026-10-05 — generalized title, per explicit instruction: this is
          PGx-only today (the only source/target template pair configured),
          but the tool itself isn't PGx-specific — hardcoding that into the
          page title would need fixing again the moment a second
          requisition type is added. */}
      <TopBar subtitle="REQUISITION CONVERSION" title="Convert a Third-Party Requisition" />
      <Box sx={{ maxWidth: 1100, mx: "auto", px: 3, pt: 2, display: "flex", borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.08)"}` }}>
        {tabBtn(0, "New Conversion")}
        {tabBtn(1, "Find Previous Conversion")}
      </Box>
      {tab === 0 ? (
        <NewConversionTab isDark={isDark} resumeConversionId={resumeConversionId} onResumed={() => setResumeConversionId(null)} />
      ) : (
        <FindPreviousTab isDark={isDark} onResume={(conversionId) => { setResumeConversionId(conversionId); setTab(0); }} />
      )}
    </Box>
  );
}
